/**
 * Sign in.
 *
 * There is deliberately no "create an account" link. The product is
 * invite-only, and offering a sign-up route that always fails would be a worse
 * experience than not offering one — the way in is a code from a friend.
 */

import { useState } from "react";
import { useT } from "../../lib/i18n.js";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useAuth } from "../../lib/auth.js";
import { errorCode, errorMessage } from "../../lib/trpc.js";
import { Button } from "../../components/ui/Button.js";
import { Logo } from "../../components/ui/Logo.js";
import { Field } from "../../components/ui/Field.js";
import { Input } from "../../components/ui/Input.js";
import "./auth.css";

export function LoginPage() {
  const t = useT();
  const auth = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: "/login" });

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await auth.signIn(username, password, needsTotp ? totpCode : undefined);
      await navigate({ to: search.redirect ?? "/" });
    } catch (caught) {
      // The server asks for a second factor by returning a specific code
      // rather than by leaking that this account has TOTP enabled up front.
      if (errorCode(caught) === "TOTP_REQUIRED") {
        setNeedsTotp(true);
        setError(null);
      } else {
        setError(errorMessage(caught));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fc-auth">
      <form className="fc-auth__card" onSubmit={submit}>
        <header className="fc-auth__header">
          <h1 className="fc-auth__title">
            <Logo size={28} withWordmark />
          </h1>
          <p className="fc-auth__tagline">
            {t("One private place for all the memories your friends share.")}
          </p>
        </header>

        {error && (
          <div className="fc-auth__error" role="alert">
            {error}
          </div>
        )}

        {/*
          Labelled for both, because either works. Saying only "Username" to
          somebody who signed up with an email sends them to the wrong
          conclusion — that they have the wrong account rather than the wrong
          field.
        */}
        <Field label={t("Username or email")} htmlFor="username">
          <Input
            id="username"
            name="username"
            autoComplete="username"
            autoFocus
            required
            value={username}
            onChange={(event) => setUsername(event.target.value)}
          />
        </Field>

        <Field label={t("Password")} htmlFor="password">
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>

        {needsTotp && (
          <Field
            label={t("Authenticator code")}
            htmlFor="totp"
            hint={t(
              "Open your authenticator app and enter the current 6-digit code.",
            )}
          >
            <Input
              id="totp"
              name="totp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              autoFocus
              required
              value={totpCode}
              onChange={(event) =>
                setTotpCode(event.target.value.replace(/\D/g, ""))
              }
            />
          </Field>
        )}

        <Button type="submit" variant="primary" loading={busy}>
          {t("Sign in")}
        </Button>

        <Link to="/recover" className="fc-auth__link">{t("Forgot your password?")}</Link>

        <p className="fc-auth__footnote">
          MyFriends.Cloud is invite-only.{" "}
          <button
            type="button"
            className="fc-auth__link"
            onClick={() =>
              void navigate({ to: "/register", search: { code: undefined } })
            }
          >
            {t("Have an access code?")}
          </button>
        </p>
      </form>
    </div>
  );
}
